import { FC, useState } from 'react';
import { useAsync } from 'react-use';
import { CircularProgress } from '@getgo/chameleon-web-react-wrapper';

import { useAppDispatch, useAppSelector } from 'hooks';
import useProvisioning from 'hooks/useProvisioning';
import { AdyenWrapper, loadAdyenACHCheckout } from 'lib/adyen-client';
import { showErrorSnack } from 'modules/error';
import { accountKey, locale } from 'modules/global-wrapper';
import { isNewTabPayment, sessionInvoiceNumber, sessionSuccessRedirectUrl } from 'modules/session-details';
import Track, { OnePayEventNames, PaymentMethodEventNames } from 'modules/tracking';
import { postTransactionAdyenFinalize, postTransactionAdyenPrepare } from 'modules/transactions-cc';
import { FinalizeAdyenResponse, PrepareAdyenResp } from 'types/transactions-cc';
import {
  ACH_ADD_PAY,
  COPAS_PAYMENT_STATUS,
  PAYMENT_METHODS_TYPES,
  SEARCH_PARAMS,
  TRACKING_EVENT_STATUS,
} from 'utils/constants';
import st from 'utils/shared-translations';

import './adyen-ach-form.css';

interface AdyenACHFormProps {
  intent: string;
}

let AdyenClient: AdyenWrapper | undefined = undefined;
let ach: any = undefined;

const loadExternalScripts = async (prepareResp: PrepareAdyenResp) => {
  const { adyenJsSettings } = prepareResp;
  AdyenClient = await loadAdyenACHCheckout(adyenJsSettings);
};

const AdyenACHForm: FC<AdyenACHFormProps> = ({ intent }) => {
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(true);

  const { processQuote, processRenewal, processInvoice } = useProvisioning();

  const selectedGlobalAccountKey = useAppSelector(accountKey);
  const selectedGlobalLocale = useAppSelector(locale);

  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';

  // Session details selectors
  const selectedSuccessRedirectUrl = useAppSelector(sessionSuccessRedirectUrl);
  const selectedIsNewTabPayment = useAppSelector(isNewTabPayment);

  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const onPaymentCompleted = async (result, component, prepareResp) => {
    try {
      const response: FinalizeAdyenResponse = await dispatch(
        postTransactionAdyenFinalize({
          checkoutSessionId: prepareResp.adyenSessionId,
          checkoutSessionResult: result.sessionResult,
          setAsDefault: false,
          paymentMethod: 'ach',
        }),
      ).unwrap();
      if (response.status !== COPAS_PAYMENT_STATUS.SUCCESS) {
        throw new Error('Adyen finalization failed');
      } else if (intent === ACH_ADD_PAY) {
        if (selectedIsNewTabPayment) window.close();
        else if (renewalDate) processRenewal(PAYMENT_METHODS_TYPES.ach);
        else if (selectedInvoiceNumber) processInvoice(PAYMENT_METHODS_TYPES.ach);
        else processQuote(PAYMENT_METHODS_TYPES.ach);
      } else {
        window.location.assign(selectedSuccessRedirectUrl);
      }
    } catch {
      Track(PaymentMethodEventNames.achSaveCTA, { status: TRACKING_EVENT_STATUS.copasFailure });
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  const onError = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenACHFormError, { error });
  };

  const onPaymentFailed = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenACHPaymentFailed, { error });
  };

  const initializeAdyenCard = async (prepareResp: PrepareAdyenResp) => {
    const configuration = {
      environment: prepareResp.adyenEnvironment,
      clientKey: prepareResp.adyenClientKey,
      session: {
        id: prepareResp.adyenSessionId,
        sessionData: prepareResp.adyenSessionData,
      },
      onPaymentCompleted: (result, component) => onPaymentCompleted(result, component, prepareResp),
      onError,
      onPaymentFailed,
    };
    try {
      if (AdyenClient) {
        ach = await AdyenClient.initialize(configuration);
        setLoading(false);
        ach.mount('#adyen-ach-form');
      }
    } catch {
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  useAsync(async () => {
    if (selectedGlobalAccountKey) {
      try {
        const resp: PrepareAdyenResp = await dispatch(
          postTransactionAdyenPrepare({
            paymentMethodKey: null,
            browserLocale: selectedGlobalLocale,
            returnUrl: window.location.origin,
            paymentMethod: 'ach',
          }),
        ).unwrap();
        await loadExternalScripts(resp);
        initializeAdyenCard(resp);
      } catch {
        Track(PaymentMethodEventNames.prepareACHResponse, { status: TRACKING_EVENT_STATUS.copasFailure });
        dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
      }
    }
  }, [selectedGlobalAccountKey]);

  return (
    <>
      <div id="adyen-ach-form"></div>
      {isLoading && (
        <div className="adyen-ach-form-loader">
          <CircularProgress />
        </div>
      )}
    </>
  );
};

export default AdyenACHForm;
